import { put, select, takeLatest } from 'redux-saga/effects'

import { getAppliedVouchers, getVoucherSessionId } from 'selectors'

import { common, voucher as voucherActions } from '../constants/actions'
import { voucher as voucherEndPoints } from '../constants/endpoints'
import { post } from '../utils/request'

export function* validateVoucher({ payload, callback }) {
  const { memberId, pin, voucherCode, voucherType, voucherId, provider } = payload

  const { issuer } = provider || {}

  let voucherError = null
  let voucherValidated = false
  let voucherBalance = 0

  let validationEndpoint = null
  let validationData = {}

  // eslint-disable-next-line default-case
  switch (issuer) {
    case 'NSW':
      validationEndpoint = voucherEndPoints.validateNswVoucher
      validationData = {
        voucherCode,
        voucherType,
        voucherId,
      }
      break
    case 'VIC':
      validationEndpoint = voucherEndPoints.validateVicVoucher
      validationData = {
        voucherCode,
        isFromAdmin: false,
      }
      break
  }

  if (validationEndpoint) {
    yield put(common.setShowLoadingIndicator(true))

    const response = yield post({
      endpointId: validationEndpoint,
      data: validationData,
    })

    yield put(common.setShowLoadingIndicator(false))

    const { success, data, errorMessage } = response || {}
    const { success: dataSuccess, balance, error, message, code } = data || {}
    voucherValidated = balance ? !!success : !!dataSuccess
    voucherBalance = balance || 0
    voucherError = errorMessage || message || error || code || 'Something went wrong'
  }

  if (voucherValidated) {
    yield put(
      voucherActions.validateSucceeded({
        memberId,
        pin,
        voucherId,
        voucherType,
        provider,
        appliedVoucher: voucherCode,
        balance: voucherBalance,
        validated: true,
        sessionValidated: false,
        isLoading: true,
        message: '',
      })
    )

    yield put(voucherActions.validateVoucherSession({ memberId }, callback))
  } else {
    yield put(
      voucherActions.validateFailed({
        memberId,
        error: voucherError,
      })
    )
  }
}

// eslint-disable-next-line consistent-return
export function* validateVoucherBeforeProceed({ payload, callback }) {
  const session = payload
  const appliedVouchers = yield select(getAppliedVouchers)

  const requests = Object.entries(appliedVouchers).map(([memberId, voucher]) => {
    const { pin, voucherType, voucherId, voucherIssuer, appliedVoucher, provider } = voucher

    return {
      memberId,
      pin,
      voucherType,
      voucherId,
      voucherIssuer,
      voucherCode: appliedVoucher,
      provider,
    }
  })

  // biome-ignore lint/complexity/useOptionalChain: TODO(biome-2.4): newly detected in 2.4 upgrade
  if (!requests || !requests.length) {
    return callback(true)
  }

  let validationSuccess = true

  yield put(common.setShowLoadingIndicator(true))

  // eslint-disable-next-line no-restricted-syntax
  for (const request of requests) {
    if (request && Object.keys(request).length) {
      const { memberId } = request
      const { provider, ...restRequest } = request
      const { issuer } = provider

      let validationEndpoint = null
      switch (issuer) {
        // biome-ignore lint/suspicious/useDefaultSwitchClauseLast: TODO
        default:
        // biome-ignore lint/complexity/noUselessSwitchCase: TODO
        case 'NSW':
          validationEndpoint = voucherEndPoints.validateNswVoucher
          break
        case 'VIC':
          validationEndpoint = voucherEndPoints.validateVicVoucher
          break
      }

      const response = yield post({
        endpointId: validationEndpoint,
        data: restRequest,
      })

      if (!response.success) {
        validationSuccess = false

        yield put(
          voucherActions.validateFailed({
            memberId,
            error: response.errorMessage,
          })
        )
      }
    }
  }

  const sessionResponse = yield post({
    endpointId: voucherEndPoints.session,
    data: {
      session,
      vouchers: requests.map(req => ({
        voucherCode: req.voucherCode,
        voucherId: req.voucherId,
      })),
    },
  })

  if (!sessionResponse.success) {
    validationSuccess = false
  }

  yield put(common.setShowLoadingIndicator(false))

  callback(validationSuccess)
}

export function* removeVoucher({ payload, callback }) {
  const memberId = payload

  yield put(voucherActions.removeVoucherData(memberId))
  yield put(voucherActions.updateVoucherSession())

  const appliedVouchers = yield select(getAppliedVouchers)

  if (typeof callback === 'function') {
    callback(appliedVouchers)
  }

  return appliedVouchers
}

export function* redeemVouchers({ payload }) {
  // eslint-disable-next-line no-restricted-globals
  // biome-ignore lint/suspicious/noGlobalIsNan: TODO
  if (!payload || isNaN(payload)) {
    return
  }

  yield post({
    endpointId: voucherEndPoints.redeemVoucher,
    data: {
      orderId: payload,
    },
  })
}

export function* updateVoucherSession() {
  const session = yield select(getVoucherSessionId)
  const appliedVouchers = yield select(getAppliedVouchers)

  const data = {
    session,
    vouchers: Object.values(appliedVouchers).map(voucher => {
      const { voucherId, appliedVoucher: voucherCode } = voucher

      return { voucherId, voucherCode }
    }),
  }

  yield post({
    endpointId: voucherEndPoints.session,
    data,
  })
}

export function* validateVoucherSession({ payload, callback }) {
  const { memberId } = payload
  const session = yield select(getVoucherSessionId)
  const appliedVouchers = yield select(getAppliedVouchers)

  const vouchers = Object.values(appliedVouchers).map(voucher => {
    const { voucherId, appliedVoucher: voucherCode } = voucher

    return { voucherId, voucherCode }
  })

  if (!payload || !vouchers.length) return

  const data = {
    session,
    vouchers,
  }

  const response = yield post({
    endpointId: voucherEndPoints.session,
    data,
  })

  if (response.success) {
    yield put(
      voucherActions.validateSucceeded({
        memberId,
        sessionValidated: true,
        isLoading: false,
      })
    )

    callback(response)
  } else {
    const { errorMessage } = response || {}
    const { message } = errorMessage || {}

    yield put(
      voucherActions.validateFailed({
        memberId,
        error: message || errorMessage || 'Something went wrong',
      })
    )

    callback({
      message: 'This voucher has been used in other session',
    })
  }
}

export function* freeVouchers({ payload }) {
  let session = payload
  if (!session) {
    session = yield select(getVoucherSessionId)
  }
  if (session) {
    yield post({
      endpointId: voucherEndPoints.session,
      data: {
        vouchers: [],
        session,
      },
    })
  }
}

export function* runVoucherSagas() {
  yield takeLatest(voucherActions.VALIDATE_VOUCHER, validateVoucher)
  yield takeLatest(voucherActions.VALIDATE_BEFORE_PROCEED, validateVoucherBeforeProceed)
  yield takeLatest(voucherActions.REMOVE_VOUCHER, removeVoucher)
  yield takeLatest(voucherActions.REDEEM_VOUCHERS, redeemVouchers)
  yield takeLatest(voucherActions.VALIDATE_VOUCHER_SESSION, validateVoucherSession)
  yield takeLatest(voucherActions.UPDATE_VOUCHER_SESSION, updateVoucherSession)
  yield takeLatest(voucherActions.RESET_VOUCHER, freeVouchers)
}
